/**
 * Sessions-by-establishment plot: filters, fetch, empty state or stacked bar.
 */

import * as React from 'react';
import {
  HorizontalStackedBarChart,
  type BarChartData,
} from '@/components/common/stats/HorizontalStackedBarChart';
import {
  ALL_SESSION_STATUS_KEYS,
  CHART_LABELS,
  CHART_SKELETON_HEIGHT,
  SESSION_STATUS_SERIES_COLORS,
  type ChartHeaderFilterProps,
} from '@/components/charts/chart-configs';
import { SessionsChartFilters } from '@/components/charts/sessions/SessionsChartFilters';
import {
  buildSessionsChartView,
  chartHeightForCount,
  hasChartSeriesData,
  resolveSessionsChartFilterVisibility,
  sessionsChartCountTitle,
  toggleSessionStatus,
} from '@/components/charts/sessions/sessions-chart-utils';
import { useDashboardCharts } from '@/hooks/useDashboardCharts';
import { useInterfaceElements } from '@/hooks/useInterfaceElements';
import { getErrorMessage } from '@/utils/errors-utils';
import {
  DEFAULT_DASHBOARD_STATS_DATE_FILTERS,
  buildDashboardStatsDateQuery,
  type DashboardStatsDateFilterState,
} from '@/utils/dashboard-stats-date-range-utils';

/** Empty plot when no session series match the filters. */
function renderEmpty(height: number): React.ReactNode {
  return (
    <div
      className="flex flex-col items-center justify-center gap-2 text-muted-foreground rounded-xl border bg-card/60"
      style={{ height }}
    >
      <p className="text-sm font-medium">{CHART_LABELS.sessionsEmpty}</p>
      <p className="text-xs opacity-70">{CHART_LABELS.sessionsEmptyHint}</p>
    </div>
  );
}

/** Sessions stacked bar with its own date / status filters. */
export const SessionsChart: React.FC<ChartHeaderFilterProps> = ({
  showFilters,
  onHasFiltersChange,
}) => {
  const { elements: dashboardElements = [] } = useInterfaceElements('dashboard');
  const visibility = resolveSessionsChartFilterVisibility(dashboardElements);
  const [dateFilters, setDateFilters] = React.useState<DashboardStatsDateFilterState>(
    DEFAULT_DASHBOARD_STATS_DATE_FILTERS,
  );
  const [sessionStatusFilter, setSessionStatusFilter] =
    React.useState<string[]>(ALL_SESSION_STATUS_KEYS);

  const chartsApiQuery = React.useMemo(
    () => buildDashboardStatsDateQuery(dateFilters),
    [dateFilters],
  );
  const { data: chartsData, isLoading, isError } = useDashboardCharts(chartsApiQuery);

  const { chartData, displayCount } = React.useMemo(
    () =>
      buildSessionsChartView(
        chartsData?.sessionsByEstablishment as BarChartData | undefined,
        sessionStatusFilter,
      ),
    [chartsData, sessionStatusFilter],
  );
  const height = chartHeightForCount(displayCount);
  const countTitle = sessionsChartCountTitle(displayCount);

  React.useEffect(() => {
    onHasFiltersChange(!isLoading && (isError || visibility.hasAnyFilter));
  }, [isLoading, isError, visibility.hasAnyFilter, onHasFiltersChange]);

  function handleToggleStatus(status: string): void {
    setSessionStatusFilter((prev) => toggleSessionStatus(prev, status));
  }

  function renderFilters(): React.ReactNode {
    if (!showFilters) return null;
    return (
      <SessionsChartFilters
        filters={dateFilters}
        onChange={setDateFilters}
        visibility={visibility}
        sessionStatuses={sessionStatusFilter}
        onToggleStatus={handleToggleStatus}
      />
    );
  }

  function renderSubtitle(): React.ReactNode {
    if (isLoading || isError || displayCount <= 0) return null;
    return (
      <p className="text-xs text-muted-foreground mb-2">
        {countTitle} · {CHART_LABELS.sessionsSubtitle}
      </p>
    );
  }

  function renderPlot(): React.ReactNode {
    if (isLoading) {
      return (
        <div
          className="rounded-xl border bg-card p-4 shadow-sm animate-pulse"
          style={{ height: CHART_SKELETON_HEIGHT }}
        />
      );
    }
    if (isError) {
      return (
        <div className="flex items-center justify-center h-40 text-muted-foreground text-sm rounded-xl border bg-card">
          {getErrorMessage('loadChartFailed')}
        </div>
      );
    }
    if (!hasChartSeriesData(chartData)) return renderEmpty(height);
    return (
      <HorizontalStackedBarChart
        data={chartData}
        height={height}
        seriesColors={SESSION_STATUS_SERIES_COLORS}
      />
    );
  }

  return (
    <>
      {renderFilters()}
      {renderSubtitle()}
      {renderPlot()}
    </>
  );
};
